import React from 'react';
import { reportError } from '@/lib/telemetry';
import { routes } from '@/routes';
import {
  isVitePreloadReloadPending,
  subscribeToVitePreloadRecovery,
} from '@/lib/runtime/vitePreloadErrorRecovery';
import { RootHydrateFallback } from '../RouteHydrateFallbacks/RootHydrateFallback';
import { ActionableErrorState } from './ActionableErrorState';

interface AppErrorBoundaryProps {
  children: React.ReactNode;
  reloadPage?: () => void;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

export class AppErrorBoundary extends React.Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    reportError({
      error,
      source: 'AppErrorBoundary',
      severity: 'fatal',
      react: {
        componentStack: info.componentStack ?? undefined,
      },
    });
  }

  render() {
    if (this.state.hasError) {
      return <AppErrorFallback reloadPage={this.props.reloadPage} />;
    }

    return this.props.children;
  }
}

function AppErrorFallback({ reloadPage }: Pick<AppErrorBoundaryProps, 'reloadPage'>) {
  const reloadPending = React.useSyncExternalStore(
    subscribeToVitePreloadRecovery,
    isVitePreloadReloadPending,
    () => false
  );

  if (reloadPending) return <RootHydrateFallback />;

  const isEmbedded = new URLSearchParams(globalThis.location?.search ?? '').has('partner_id');
  return (
    <main className="flex min-h-screen-safe items-center justify-center px-5 py-12">
      <ActionableErrorState
        title="This page hit a problem"
        message="Any saved progress is still safe. Reload the page, or choose another place to continue."
        actions={[
          {
            label: 'Reload page',
            onSelect: reloadPage ?? (() => window.location.reload()),
            emphasis: 'primary',
          },
          ...(isEmbedded
            ? [
                {
                  label: 'Open Atrivial',
                  href: routes.home,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                },
                {
                  label: 'Contact support',
                  href: routes.contactUs,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                },
              ]
            : [
                { label: 'Go home', href: routes.home, target: '_top' },
                {
                  label: 'Contact support',
                  href: routes.contactUs,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                },
              ]),
        ]}
      />
    </main>
  );
}
